import { ArrowPathIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectDetailResponse } from '@/entities/project/api/generated/model';
import { useGetReferenceChatAvailabilities } from '@/entities/references/api/generated/api';
import { ReferenceChatType } from '@/entities/references/api/generated/model';
import { isReferenceChatNotSupported } from '@/entities/references/lib/is-reference-chat-not-supported';
import { MatchingSkillCard } from '@/features/ai-matching-results/ui/matching-skill-card/MatchingSkillCard';
import {
  AiIconButton,
  Banner,
  Button,
  Card,
  Dialog,
  DropdownMenu,
  Skeleton,
  Text,
  useSkeletonMinDisplay,
} from '@/shared/ui';
import { NotSupportedAIAnalysisContent } from './NotSupportedAIAnalysisContent';
import type { ProjectReferenceChatType } from './types';
import { useInlineReferenceChat } from './useInlineReferenceChat';

type Props = {
  project: ProjectDetailResponse;
};

// NOTE: ReferenceChatType.revenue_expenditure_account_items は汎用AIチャット(general_chat)の
// ナレッジ追加ドロップダウン専用のため、事業詳細の参考事業検索UIには表示しない。
const ALL_TYPES: ProjectReferenceChatType[] = [
  ReferenceChatType.prefectural_special_allocation_taxes,
  ReferenceChatType.national_grants,
  ReferenceChatType.prefectural_grants,
];

// 動的キー生成禁止ルールに準拠
const useReferenceChatLabels = () => {
  const { t } = useTranslation();
  return {
    [ReferenceChatType.national_grants]: {
      label: t('ai.national-grants.link-label'),
      title: t('ai.national-grants.accordion-title'),
      description: t('ai.national-grants.accordion-description'),
    },
    [ReferenceChatType.prefectural_grants]: {
      label: t('ai.prefectural-grants.link-label'),
      title: t('ai.prefectural-grants.accordion-title'),
      description: t('ai.prefectural-grants.accordion-description'),
    },
    [ReferenceChatType.prefectural_special_allocation_taxes]: {
      label: t('ai.prefectural-special-allocation-taxes.link-label'),
      title: t('ai.prefectural-special-allocation-taxes.accordion-title'),
      description: t('ai.prefectural-special-allocation-taxes.accordion-description'),
    },
  };
};

export const MatchingTabContent = ({ project }: Props) => {
  const { t } = useTranslation();
  const labels = useReferenceChatLabels();
  const { data: availabilities, isLoading } = useGetReferenceChatAvailabilities();
  const showSkeleton = useSkeletonMinDisplay(isLoading);

  const [reExecuteTarget, setReExecuteTarget] = useState<ProjectReferenceChatType | null>(null);

  // 3つのフックを明示的に呼び出し（フックのループ呼び出し禁止のため）
  const nationalGrantsChat = useInlineReferenceChat({
    projectId: project.id,
    chatType: ReferenceChatType.national_grants,
  });
  const prefecturalGrantsChat = useInlineReferenceChat({
    projectId: project.id,
    chatType: ReferenceChatType.prefectural_grants,
  });
  const prefecturalSpecialTaxChat = useInlineReferenceChat({
    projectId: project.id,
    chatType: ReferenceChatType.prefectural_special_allocation_taxes,
  });

  const chatStates = {
    [ReferenceChatType.national_grants]: nationalGrantsChat,
    [ReferenceChatType.prefectural_grants]: prefecturalGrantsChat,
    [ReferenceChatType.prefectural_special_allocation_taxes]: prefecturalSpecialTaxChat,
  };

  const isNotSupported = (chatType: ProjectReferenceChatType) =>
    isReferenceChatNotSupported({ chatType, availabilities });

  // 判定できる候補が1件でもある（実行済み & 候補あり）ときだけ判定バナーを出す。
  // 未実行・実行中・候補0件のみのときは「判定すると〜」の案内が時期尚早になるため表示しない。
  const hasJudgeableCandidates = ALL_TYPES.some((chatType) => {
    const state = chatStates[chatType];
    return state.viewStatus === 'completed' && state.candidates.length > 0;
  });

  const handleExecute = (chatType: ProjectReferenceChatType) => {
    const state = chatStates[chatType];
    if (state.viewStatus === 'completed' || state.viewStatus === 'failed-server') {
      setReExecuteTarget(chatType);
    } else {
      state.execute();
    }
  };

  const handleConfirmReExecute = () => {
    if (reExecuteTarget) {
      chatStates[reExecuteTarget].execute();
      setReExecuteTarget(null);
    }
  };

  // 実行ボタン（アクセシブルな div + role="button" でカード本体内に表示）
  const renderExecuteButton = (chatType: ProjectReferenceChatType) => {
    const state = chatStates[chatType];
    const isDisabled = state.isExecuting || state.isTokenLimitExceeded;

    const handleClick = () => {
      if (isDisabled) return;
      handleExecute(chatType);
    };

    return (
      <AiIconButton size="sm" disabled={isDisabled} onClick={handleClick}>
        {t('common.actions.execute')}
      </AiIconButton>
    );
  };

  // ヘッダーに表示する操作メニュー（⋮）or 実行中インジケータ
  const renderHeaderAction = (chatType: ProjectReferenceChatType) => {
    const state = chatStates[chatType];
    if (state.viewStatus === 'loading') {
      return (
        <div className="flex items-center gap-1 rounded-full border border-primary px-3 py-1 text-primary text-xs">
          <ArrowPathIcon className="size-3.5 animate-spin" />
          {t('ai.analysis.executing')}
        </div>
      );
    }

    const isExecuteDisabled = state.isExecuting || state.isTokenLimitExceeded;
    const menuItems = (() => {
      switch (state.viewStatus) {
        case 'completed':
        case 'failed-server':
          return [
            {
              key: 're-execute',
              item: t('common.actions.re-execute'),
              onClick: () => handleExecute(chatType),
              disabled: isExecuteDisabled,
            },
          ];
        case 'failed-network':
          return [
            {
              key: 'reload',
              item: t('common.actions.reload'),
              onClick: () => state.retry(),
            },
          ];
        default:
          // not-executed
          return [
            {
              key: 'execute',
              item: t('common.actions.execute'),
              onClick: () => handleExecute(chatType),
              disabled: isExecuteDisabled,
            },
          ];
      }
    })();

    return (
      <DropdownMenu menuItems={menuItems}>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('common.actions.operations-menu', { name: labels[chatType].title })}
        >
          <EllipsisVerticalIcon className="size-4" />
        </Button>
      </DropdownMenu>
    );
  };

  const renderRetryAction = (chatType: ProjectReferenceChatType) => (
    <Button size="sm" variant="outline" onClick={() => chatStates[chatType].retry()}>
      {t('common.actions.reload')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      {hasJudgeableCandidates && (
        <Banner
          type="info"
          title={t('ai-matching-results.project-detail.judgement-banner')}
          showTypeIcon
        />
      )}

      {showSkeleton ? (
        <div className="space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {ALL_TYPES.map((chatType) => {
            if (isNotSupported(chatType)) {
              return (
                <Card key={chatType} className="flex flex-col gap-4 p-4">
                  <Text variant="label">{labels[chatType].title}</Text>
                  <NotSupportedAIAnalysisContent chatType={chatType} />
                </Card>
              );
            }

            const state = chatStates[chatType];
            // 判定値は API 由来（candidates の judgement）を単一ソースとして制御モードで渡す
            const judgements = Object.fromEntries(
              state.candidates.map((candidate) => [candidate.candidateId, candidate.judgement]),
            );
            // key に project.id を含めて事業切替時にリマウントする
            return (
              <MatchingSkillCard
                key={`${project.id}:${chatType}`}
                skillTitle={labels[chatType].title}
                description={labels[chatType].description}
                candidates={state.candidates}
                judgements={judgements}
                onJudgementChange={state.updateJudgement}
                viewStatus={state.viewStatus}
                headerAction={renderHeaderAction(chatType)}
                notExecutedAction={renderExecuteButton(chatType)}
                retryAction={renderRetryAction(chatType)}
              />
            );
          })}
        </div>
      )}

      <Dialog
        open={reExecuteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setReExecuteTarget(null);
        }}
        onConfirm={handleConfirmReExecute}
        title={t('ai.analysis.re-execute-dialog-title')}
        confirmLabel={t('common.actions.re-execute')}
      >
        <Text>
          {reExecuteTarget
            ? t('ai.analysis.re-execute-dialog-description', {
                name: labels[reExecuteTarget].label,
              })
            : ''}
        </Text>
      </Dialog>
    </div>
  );
};
